import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { ApiError, getJSON } from '@/lib/api'
import { PortalPersonName } from './PortalPersonName'
import type { PortalPersonActivity, PortalPersonResponse } from './portalPeople.types'

export function MemberPortalPersonActivity({
  organizationId,
  viewer,
  personKey,
  onBack,
}: {
  organizationId: number
  viewer: string
  personKey: string
  onBack: () => void
}) {
  const path = `/api/v1/member_portal/organizations/${organizationId}/dashboard/people/${encodeURIComponent(personKey)}`
  const query = useQuery({
    queryKey: ['member-portal-dashboard', viewer, organizationId, 'person', path],
    queryFn: () => getJSON<PortalPersonResponse>(path),
    retry: false,
  })
  const missing = query.error instanceof ApiError && query.error.status === 404
  return (
    <div className="space-y-4">
      <Button
        variant="ghost"
        size="sm"
        className="-ml-3 gap-1 text-muted-foreground"
        onClick={onBack}
      >
        <ArrowLeft aria-hidden="true" className="size-4" />
        Back to people
      </Button>
      {query.isPending && <EmptyState variant="quiet" title="Loading activity" />}
      {missing && (
        <EmptyState variant="quiet" title="This person is not in your organization's list" />
      )}
      {query.isError && !missing && (
        <Alert variant="destructive">
          Activity unavailable.{' '}
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </Alert>
      )}
      {query.data && <PersonActivity person={query.data.person} />}
    </div>
  )
}

function PersonActivity({ person }: { person: PortalPersonActivity }) {
  return (
    <>
      <div>
        <h2 className="text-subheading font-semibold">
          <PortalPersonName name={person.name} status={person.person_status} />
        </h2>
        <p className="mt-1 text-body-sm text-muted-foreground">
          {person.membership_level
            ? `Membership: ${person.membership_level}`
            : 'No personal membership'}
        </p>
      </div>
      <ActivityCard
        title="Groups"
        empty="No current group memberships"
        count={person.groups.length}
      >
        {person.groups.map((group) => (
          <li key={group} className="px-4 py-2 text-body-sm">
            {group}
          </li>
        ))}
      </ActivityCard>
      <ActivityCard
        title="Event registrations"
        empty="No event registrations"
        count={person.events.length}
      >
        {person.events.map((event, index) => (
          <li
            key={`${event.name}:${index}`}
            className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-4 py-2 text-body-sm"
          >
            <span>
              <span className="font-medium">{event.name}</span>
              <span className="text-muted-foreground"> · {event.date_label}</span>
            </span>
            <span className="text-muted-foreground">{event.ticket ?? 'No ticket type'}</span>
          </li>
        ))}
      </ActivityCard>
      <ActivityCard title="Learning" empty="No learning records" count={person.learning.length}>
        {person.learning.map((item, index) => (
          <li
            key={`${item.course}:${index}`}
            className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-2 text-body-sm"
          >
            <span className="font-medium">{item.course}</span>
            <span className="flex items-center gap-2">
              {item.grade && <span className="text-muted-foreground">{item.grade}</span>}
              <Badge variant={item.status === 'completed' ? 'success' : 'neutral'}>
                {item.status === 'completed' ? 'Completed' : 'In progress'}
              </Badge>
            </span>
          </li>
        ))}
      </ActivityCard>
    </>
  )
}

function ActivityCard({
  title,
  empty,
  count,
  children,
}: {
  title: string
  empty: string
  count: number
  children: ReactNode
}) {
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-border px-4 py-3">
        <h3 className="text-body font-medium">{title}</h3>
      </div>
      {count ? (
        <ul className="divide-y divide-border">{children}</ul>
      ) : (
        <p className="px-4 py-3 text-body-sm text-muted-foreground">{empty}</p>
      )}
    </Card>
  )
}
